import React, { useState, useRef } from 'react';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaCode,
  FaLink,
  FaHeading,
  FaListUl,
  FaListOl,
  FaCheckSquare,
  FaQuoteRight,
  FaCodeBranch,
  FaImage,
  FaPaperclip,
  FaTable,
  FaColumns,
  FaLightbulb,
  FaChevronRight,
  FaMinus
} from 'react-icons/fa';
import { uploadApi } from '../services/api';
import { BLOCK_TYPES } from '../utils/blockTypes';
import '../styles/EditorToolbar.css';

// Helper function to create text format buttons
const Text = (formatType, icon, title) => ({
  icon,
  action: (onFormatText) => onFormatText(formatType),
  title: title || `Format as ${formatType}`,
  formatType
});

// Create format button configurations
const createFormatButtons = (onFormatText, onAddLink) => {
  const formatConfigs = [
    Text('bold', <FaBold />, 'Bold (Ctrl+B)'),
    Text('italic', <FaItalic />, 'Italic (Ctrl+I)'),
    Text('underline', <FaUnderline />, 'Underline (Ctrl+U)'),
    Text('strikethrough', <FaStrikethrough />, 'Strikethrough'),
    Text('code', <FaCode />, 'Inline Code'),
  ];

  return [
    ...formatConfigs.map(config => ({
      ...config,
      action: () => onFormatText(config.formatType)
    })),
    {
      icon: <FaLink />,
      action: onAddLink,
      title: 'Add Link Block'
    }
  ];
};

const EditorToolbar = ({
  onAddBlock,
  onFormatText,
  onUploadImage,
  onUploadFile,
  selectedBlockType
}) => {
  const [uploading, setUploading] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const handleFileUpload = async (event, type = 'file') => {
    const file = event.target.files[0];
    if (!file) return;
file
    setUploading(true);
    try {
      const response = await uploadApi.uploadFile(file, type);
      const fileUrl = response.data.data.url;

      if (type === 'image') {
        onUploadImage(fileUrl, file.name);
      } else {
        onUploadFile(fileUrl, file.name, file.size);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleAddLink = () => {
    setShowLinkDialog(true);
  };

  const handleSubmitLink = () => {
    if (linkUrl) {
      // Format the URL
      let finalUrl = linkUrl.trim();
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = `https://${finalUrl}`;
      }

      // Pass link in properties, text as content
      onAddBlock(BLOCK_TYPES.LINK, linkText || finalUrl, {
        link: [[finalUrl]],  // This goes in properties
        text: linkText || finalUrl
      });

      setLinkUrl('');
      setLinkText('');
      setShowLinkDialog(false);
    }
  };

  const handleCancelLink = () => {
    setLinkUrl('');
    setLinkText('');
    setShowLinkDialog(false);
  };

  const handleAddTable = () => {
    const rows = 3; // Default rows
    const cols = 3; // Default columns

    if (rows > 0 && cols > 0) {
      onAddBlock(BLOCK_TYPES.TABLE, '', { rows, cols });
    }
  };

  // Create format buttons using the helper function
  const formatButtons = createFormatButtons(onFormatText, handleAddLink);

  const blockButtons = [
    {
      label: 'Text',
      icon: 'T',
      action: () => onAddBlock(BLOCK_TYPES.TEXT),
      type: BLOCK_TYPES.TEXT
    },
    {
      label: 'Heading 1',
      icon: 'H1',
      action: () => onAddBlock(BLOCK_TYPES.HEADER, '', { level: 1 }),
      type: BLOCK_TYPES.HEADER
    },
    {
      label: 'Heading 2',
      icon: 'H2',
      action: () => onAddBlock(BLOCK_TYPES.SUB_HEADER),
      type: BLOCK_TYPES.SUB_HEADER
    },
    {
      label: 'Heading 3',
      icon: 'H3',
      action: () => onAddBlock(BLOCK_TYPES.SUB_SUB_HEADER),
      type: BLOCK_TYPES.SUB_SUB_HEADER
    },
    {
      label: 'Bulleted List',
      icon: <FaListUl />,
      action: () => onAddBlock(BLOCK_TYPES.BULLETED_LIST),
      type: BLOCK_TYPES.BULLETED_LIST
    },
    {
      label: 'Numbered List',
      icon: <FaListOl />,
      action: () => onAddBlock(BLOCK_TYPES.NUMBERED_LIST),
      type: BLOCK_TYPES.NUMBERED_LIST
    },
    {
      label: 'Todo List',
      icon: <FaCheckSquare />,
      action: () => onAddBlock(BLOCK_TYPES.TODO),
      type: BLOCK_TYPES.TODO
    },
    {
      label: 'Quote',
      icon: <FaQuoteRight />,
      action: () => onAddBlock(BLOCK_TYPES.QUOTE),
      type: BLOCK_TYPES.QUOTE
    },
    {
      label: 'Code Block',
      icon: <FaCodeBranch />,
      action: () => onAddBlock(BLOCK_TYPES.CODE),
      type: BLOCK_TYPES.CODE
    },
    {
      label: 'Link',
      icon: <FaLink />,
      action: handleAddLink,
      type: BLOCK_TYPES.LINK
    },
    {
      label: 'Divider',
      icon: <FaMinus />,
      action: () => onAddBlock(BLOCK_TYPES.DIVIDER),
      type: BLOCK_TYPES.DIVIDER
    },
    {
      label: 'Callout',
      icon: <FaLightbulb />,
      action: () => onAddBlock(BLOCK_TYPES.CALLOUT),
      type: BLOCK_TYPES.CALLOUT
    },
    // {
    //   label: 'Toggle',
    //   icon: <FaChevronRight />,
    //   action: () => onAddBlock(BLOCK_TYPES.TOGGLE),
    //   type: BLOCK_TYPES.TOGGLE
    // },
    // {
    //   label: 'Columns',
    //   icon: <FaColumns />,
    //   action: () => onAddBlock(BLOCK_TYPES.COLUMN_LIST),
    //   type: BLOCK_TYPES.COLUMN_LIST
    // },
    {
      label: 'Table',
      icon: <FaTable />,
      action: handleAddTable,
      type: BLOCK_TYPES.TABLE
    },
  ];

  const mediaButtons = [
    {
      label: 'Image',
      icon: <FaImage />,
      action: () => imageInputRef.current?.click(),
      title: 'Upload Image'
    },
    {
      label: 'File',
      icon: <FaPaperclip />,
      action: () => fileInputRef.current?.click(),
      title: 'Upload File'
    }
  ];

  return (
    <div className="editor-toolbar">
      {/* Formatting Tools */}
      {/* <div className="toolbar-group">
        {formatButtons.map((btn, index) => (
          <button
            key={index}
            className="toolbar-btn"
            onClick={btn.action}
            title={btn.title}
          >
            {btn.icon}
          </button>
        ))}
      </div> */}

      {/* Block Types */}
      <div className="toolbar-group">
        <div className="dropdown">
          <button className="toolbar-btn">
            <FaHeading /> Blocks
          </button>
          <div className="dropdown-content">
            {blockButtons.map((btn, index) => (
              <button
                key={index}
                className={`dropdown-item ${selectedBlockType === btn.type ? 'active' : ''}`}
                onClick={btn.action}
              >
                <span className="dropdown-icon">
                  {typeof btn.icon === 'string' ? btn.icon : btn.icon}
                </span>
                <span>{btn.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Media Upload */}
      <div className="toolbar-group">
        {mediaButtons.map((btn, index) => (
          <button
            key={index}
            className="toolbar-btn"
            onClick={btn.action}
            title={btn.title}
            disabled={uploading}
          >
            {btn.icon} {btn.label}
          </button>
        ))}

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={imageInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={(e) => handleFileUpload(e, 'image')}
          disabled={uploading}
        />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => handleFileUpload(e, 'file')}
          disabled={uploading}
        />
      </div>

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="link-dialog-overlay">
          <div className="link-dialog">
            <h3>Add Link</h3>
            <div className="link-input-group">
              <label>URL:</label>
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                autoFocus
              />
            </div>
            <div className="link-input-group">
              <label>Text (optional):</label>
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Link text"
              />
            </div>
            <div className="link-dialog-actions">
              <button
                className="link-btn-cancel"
                onClick={handleCancelLink}
              >
                Cancel
              </button>
              <button
                className="link-btn-submit"
                onClick={handleSubmitLink}
                disabled={!linkUrl.trim()}
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Uploading Indicator */}
      {uploading && (
        <div className="uploading-indicator">
          <div className="spinner"></div>
          <span>Uploading...</span>
        </div>
      )}
    </div>
  );
};

export default EditorToolbar;